import { PROP_TYPE_HINT, type RegistryEntry } from "@/src/shared_modules/custom_elements/types/customElements";
import { DS_CUSTOM_ELEMENT_NAME, type DSCustomElementName } from "./customElementNames";
import { TEXT_FIELD_REGISTRY_ENTRY, TEXT_INPUT_REGISTRY_ENTRY } from "./textControlCustomElements";

export const DS_FORM_INPUT_CUSTOM_ELEMENTS_REGISTRY = {
  [DS_CUSTOM_ELEMENT_NAME.FileUpload]: {
    emits: ["focus", "blur", "update:modelValue", "select", "reject", "remove", "remove-all"],
    exposedMethods: ["focus", "blur", "openFileDialog", "resetInput"],
    loadComponent: () => import("@/src/design_system/form_elements/file_upload/components/DSFileUpload.vue"),
    propTypes: {
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      multiple: PROP_TYPE_HINT.Boolean,
      maxFileSize: PROP_TYPE_HINT.Number,
      maxFiles: PROP_TYPE_HINT.Number,
      fullPageDropzone: PROP_TYPE_HINT.Boolean,
      showFileList: PROP_TYPE_HINT.Boolean,
      progress: PROP_TYPE_HINT.Number,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Checkbox]: {
    emits: ["focus", "blur"],
    exposedMethods: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/checkbox/components/DSCheckbox.vue"),
    modelValueType: PROP_TYPE_HINT.Boolean,
    slots: ["label"],
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      hasError: PROP_TYPE_HINT.Boolean,
      checkedValue: PROP_TYPE_HINT.String,
      uncheckedValue: PROP_TYPE_HINT.String,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.Switch]: {
    emits: ["focus", "blur"],
    exposedMethods: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/switch/components/DSSwitch.vue"),
    modelValueType: PROP_TYPE_HINT.Boolean,
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      hasError: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      checkedValue: PROP_TYPE_HINT.String,
      uncheckedValue: PROP_TYPE_HINT.String,
    },
  },
  [DS_CUSTOM_ELEMENT_NAME.TextField]: TEXT_FIELD_REGISTRY_ENTRY,
  [DS_CUSTOM_ELEMENT_NAME.TitleField]: {
    loadComponent: () => import("@/src/design_system/form_elements/title_field/components/DSTitleField.vue"),
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      editing: PROP_TYPE_HINT.Boolean,
      defaultEditing: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      loading: PROP_TYPE_HINT.Boolean,
      maxlength: PROP_TYPE_HINT.Number,
    },
    emits: ["save", "cancel", "focus", "blur", "update:editing"],
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.PasswordField]: {
    emits: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/password_field/components/DSPasswordField.vue"),
    // Discovery would publish the component's `update:modelValue` as a bubbling, composed DOM event, handing the
    // plaintext password to every ancestor listener on the page.
    forwardComponentEmits: false,
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      readonly: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
      initiallyVisible: PROP_TYPE_HINT.Boolean,
      maxlength: PROP_TYPE_HINT.Number,
      minlength: PROP_TYPE_HINT.Number,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.UrlField]: {
    emits: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/url_field/components/DSUrlField.vue"),
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      readonly: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
      showCopyAction: PROP_TYPE_HINT.Boolean,
      showOpenAction: PROP_TYPE_HINT.Boolean,
      maxlength: PROP_TYPE_HINT.Number,
      minlength: PROP_TYPE_HINT.Number,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.TextInput]: TEXT_INPUT_REGISTRY_ENTRY,
  [DS_CUSTOM_ELEMENT_NAME.Textarea]: {
    emits: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/textarea/components/DSTextarea.vue"),
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      rows: PROP_TYPE_HINT.Number,
      disabled: PROP_TYPE_HINT.Boolean,
      readonly: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      richEditor: PROP_TYPE_HINT.Boolean,
      maxlength: PROP_TYPE_HINT.Number,
      minlength: PROP_TYPE_HINT.Number,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.NumberField]: {
    emits: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/number_field/components/DSNumberField.vue"),
    modelValueType: PROP_TYPE_HINT.Number,
    propTypes: {
      inlineEdit: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
      disabled: PROP_TYPE_HINT.Boolean,
      readonly: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      clearable: PROP_TYPE_HINT.Boolean,
      showControls: PROP_TYPE_HINT.Boolean,
      allowNegative: PROP_TYPE_HINT.Boolean,
      min: PROP_TYPE_HINT.Number,
      max: PROP_TYPE_HINT.Number,
      step: PROP_TYPE_HINT.Number,
      precision: PROP_TYPE_HINT.Number,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.SliderInput]: {
    emits: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/slider_input/components/DSSliderField.vue"),
    modelValueType: PROP_TYPE_HINT.Number,
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      min: PROP_TYPE_HINT.Number,
      max: PROP_TYPE_HINT.Number,
      step: PROP_TYPE_HINT.Number,
      showNumberField: PROP_TYPE_HINT.Boolean,
      showTickMarks: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
    },
    exposedMethods: ["focus", "blur"],
  },
  [DS_CUSTOM_ELEMENT_NAME.RadioGroup]: {
    emits: ["focus", "blur"],
    exposedMethods: ["focus", "blur"],
    loadComponent: () => import("@/src/design_system/form_elements/radio/radio_group/components/DSRadioGroup.vue"),
    modelValueType: PROP_TYPE_HINT.String,
    propTypes: {
      disabled: PROP_TYPE_HINT.Boolean,
      inlineEdit: PROP_TYPE_HINT.Boolean,
      required: PROP_TYPE_HINT.Boolean,
      hasError: PROP_TYPE_HINT.Boolean,
      hiddenLabel: PROP_TYPE_HINT.Boolean,
    },
  },
} satisfies Partial<Record<DSCustomElementName, RegistryEntry>>;
